import { useEffect, useMemo, useRef, useState } from "react";

// ─── DESIGN TOKENS (matches Sirat Log Ledger v3) ─────────────────
const C = {
  ink:   '#f4eee0',
  dim:   'rgba(244,238,224,0.62)',
  faint: 'rgba(244,238,224,0.35)',
  green: '#00e676',
  amber: '#f5b342',
  coral: '#f08a6b',
  cream: '#e9dfc7',
  cyan:  '#22d3ee',
};
const FS = "'Geist', system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
const FM = "'Geist Mono', ui-monospace, 'SF Mono', Menlo, monospace";

const SLOT_COLORS = {
  breakfast:   '#f5b342',
  lunch:       '#00e676',
  dinner:      '#f08a6b',
  snacks:      '#6ea8ff',
  supplements: '#22d3ee',
  whey:        '#4FD9C2',
};
const SLOT_LABELS = {
  breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner',
  snacks: 'Snacks',       supplements: 'Supplements', whey: 'Whey / Shakes',
};
const SLOT_ORDER = ['breakfast', 'lunch', 'dinner', 'snacks', 'supplements', 'whey'];

const VOLUMES = [200, 250];

function useCountUp(target, dur = 1300, delay = 200) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf, tid = setTimeout(() => {
      let t0;
      const step = ts => {
        if (!t0) t0 = ts;
        const p = Math.min((ts - t0) / dur, 1);
        setV(Math.round((1 - Math.pow(1 - p, 3)) * target));
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => { clearTimeout(tid); cancelAnimationFrame(raf); };
  }, [target, dur, delay]);
  return v;
}

function useRingFill(pct, delay = 250, dur = 1500) {
  const [f, setF] = useState(0);
  const mountedRef = useRef(false);
  useEffect(() => {
    if (mountedRef.current) { setF(pct); return; }
    mountedRef.current = true;
    let raf, tid = setTimeout(() => {
      let t0;
      const step = ts => {
        if (!t0) t0 = ts;
        const p = Math.min((ts - t0) / dur, 1);
        setF((1 - Math.pow(1 - p, 3)) * pct);
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => { clearTimeout(tid); cancelAnimationFrame(raf); };
  }, [pct, delay, dur]);
  return f;
}

function HeroRing({ value, max, color = C.cyan, size = 240, sw = 14, centerNode }) {
  const r = (size - sw) / 2, cx = size / 2, cy = size / 2, circ = 2 * Math.PI * r;
  // Base fill is capped at 100% in the original colour; overflow continues in
  // red on top, also capped at one full lap so we never paint past the ring.
  const ratio = max > 0 ? value / max : 0;
  // Animate green base 0→100% first, then red overflow kicks in once the
  // green has reached the rim (delay = base delay + base duration).
  const baseFill = useRingFill(Math.min(ratio, 1), 200, 1700) * circ;
  const overflowFill = useRingFill(Math.min(Math.max(0, ratio - 1), 1), 1900, 1700) * circ;
  const overflow = overflowFill > 4;
  const tipFill = overflow ? overflowFill : baseFill;
  const tipColor = overflow ? '#ff6b6b' : color;
  const endA = (tipFill / circ) * 2 * Math.PI - Math.PI / 2;
  const ex = cx + Math.cos(endA) * r, ey = cy + Math.sin(endA) * r;
  const tr = 'stroke-dasharray 1.7s cubic-bezier(0.22,0.61,0.36,1)';

  const ticks = Array.from({ length: 48 }, (_, i) => {
    const a = (i / 48) * 2 * Math.PI - Math.PI / 2, big = i % 12 === 0;
    const r1 = r + sw / 2 + 3, r2 = r + sw / 2 + (big ? 11 : 6);
    return <line key={i}
      x1={cx + Math.cos(a) * r1} y1={cy + Math.sin(a) * r1}
      x2={cx + Math.cos(a) * r2} y2={cy + Math.sin(a) * r2}
      stroke={big ? 'rgba(255,255,255,0.24)' : 'rgba(255,255,255,0.07)'}
      strokeWidth={big ? 1.5 : 0.7} />;
  });

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow: 'visible', transform: 'rotate(-90deg)' }}>
        {ticks}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={sw} />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw}
          strokeLinecap="round" strokeDasharray={`${baseFill} ${circ}`}
          style={{ transition: tr }} />
        {overflow && (
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="#ff6b6b" strokeWidth={sw}
            strokeLinecap="round" strokeDasharray={`${overflowFill} ${circ}`}
            style={{ transition: tr }} />
        )}
        {tipFill > 10 && (
          <circle cx={ex} cy={ey} r={sw / 2 + 1} fill={tipColor} />
        )}
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        {centerNode}
      </div>
    </div>
  );
}

function autoMealSlot() {
  const h = new Date().getHours();
  if (h < 11) return 'breakfast';
  if (h < 16) return 'lunch';
  if (h < 21) return 'dinner';
  return 'snacks';
}

function genId() {
  return 'pf_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

export default function ProteinFocusOverlay({
  open,
  onClose,
  totalProtein = 0,
  proteinGoal = 150,
  todayData,
  wheyProducts = [],
  milkProducts = [],
  addFoodEntry,
  today,
  onAfterLog,
}) {
  // Persisted picks
  const [selectedWheyId, setSelectedWheyId] = useState(() => {
    try { return localStorage.getItem('proteinFocusWhey') || (wheyProducts[0]?.id || ''); } catch { return wheyProducts[0]?.id || ''; }
  });
  const [milkOn, setMilkOn] = useState(() => {
    try { return localStorage.getItem('proteinFocusMilkOn') === '1'; } catch { return false; }
  });
  const [selectedMilkId, setSelectedMilkId] = useState(() => {
    try { return localStorage.getItem('proteinFocusMilk') || (milkProducts[0]?.id || ''); } catch { return milkProducts[0]?.id || ''; }
  });
  const [milkVolumeMl, setMilkVolumeMl] = useState(() => {
    try { const v = parseInt(localStorage.getItem('proteinFocusMilkMl') || '250', 10); return VOLUMES.includes(v) ? v : 250; } catch { return 250; }
  });
  const [scoopOverride, setScoopOverride] = useState(null);
  const [overrideGoal, setOverrideGoal] = useState(false); // "Add anyway" reveals calculator when goal already hit

  useEffect(() => { try { localStorage.setItem('proteinFocusWhey', selectedWheyId); } catch {} }, [selectedWheyId]);
  useEffect(() => { try { localStorage.setItem('proteinFocusMilk', selectedMilkId); } catch {} }, [selectedMilkId]);
  useEffect(() => { try { localStorage.setItem('proteinFocusMilkMl', String(milkVolumeMl)); } catch {} }, [milkVolumeMl]);
  useEffect(() => { try { localStorage.setItem('proteinFocusMilkOn', milkOn ? '1' : '0'); } catch {} }, [milkOn]);

  // Reset transient overrides when re-opening so values reflect the new state.
  useEffect(() => {
    if (open) { setScoopOverride(null); setOverrideGoal(false); }
  }, [open]);

  // Body scroll lock while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const selectedWhey = useMemo(
    () => wheyProducts.find(w => w.id === selectedWheyId) || wheyProducts[0],
    [selectedWheyId, wheyProducts]
  );
  const selectedMilk = useMemo(
    () => milkProducts.find(m => m.id === selectedMilkId) || milkProducts[0],
    [selectedMilkId, milkProducts]
  );

  // Per-mL macros derived from milk's serving baseline (servingG ≈ ml for milk).
  const milkPour = useMemo(() => {
    if (!milkOn || !selectedMilk) return null;
    const baseG = Math.max(1, selectedMilk.servingG || 250);
    const scale = milkVolumeMl / baseG;
    return {
      protein: (selectedMilk.protein || 0) * scale,
      calories: (selectedMilk.calories || 0) * scale,
      carbs: (selectedMilk.carbs || 0) * scale,
      fat: (selectedMilk.fat || 0) * scale,
      fiber: (selectedMilk.fiber || 0) * scale,
      sugar: (selectedMilk.sugar || 0) * scale,
      sodium: (selectedMilk.sodium || 0) * scale,
      label: `${milkVolumeMl}ml`,
      grams: milkVolumeMl,
    };
  }, [milkOn, selectedMilk, milkVolumeMl]);

  const remaining = Math.max(0, proteinGoal - totalProtein);
  const goalHit = remaining <= 0.5;
  const showCalculator = !goalHit || overrideGoal;

  const wheyProteinPerScoop = selectedWhey?.protein || 0;
  const wheyKcalPerScoop = selectedWhey?.calories || 0;

  // Scoops needed after milk contribution. Round to nearest so the milk
  // pour can actually shave a scoop off when its protein nudges the deficit
  // past a half-scoop boundary; otherwise ceil() always rounds up and milk
  // appears to do nothing.
  const autoScoops = (() => {
    if (!selectedWhey) return 0;
    const milkP = milkPour?.protein || 0;
    const need = remaining - milkP;
    if (need <= 0) return 0;            // milk alone covers the gap
    if (need <= wheyProteinPerScoop * 0.25) return 0; // tiny remainder — skip the scoop
    return Math.max(1, Math.round(need / Math.max(1, wheyProteinPerScoop)));
  })();
  const scoops = scoopOverride != null ? Math.max(0, scoopOverride) : autoScoops;

  const addedProtein = scoops * wheyProteinPerScoop + (milkPour?.protein || 0);
  const addedKcal = scoops * wheyKcalPerScoop + (milkPour?.calories || 0);
  const projectedTotal = totalProtein + addedProtein;
  const overshoot = Math.max(0, projectedTotal - proteinGoal);

  // Slot breakdown of current intake
  const slotBreakdown = useMemo(() => {
    const meals = todayData?.meals || {};
    return SLOT_ORDER.map(slot => {
      const entries = meals[slot] || [];
      const grams = entries.reduce((s, e) => s + (e.protein || 0), 0);
      const top = [...entries].sort((a, b) => (b.protein || 0) - (a.protein || 0)).slice(0, 2)
        .map(e => ({ name: e.name, p: Math.round((e.protein || 0) * 10) / 10 }));
      return { slot, grams, top };
    });
  }, [todayData]);
  const breakdownTotal = slotBreakdown.reduce((s, x) => s + x.grams, 0);

  const dispTotal = useCountUp(Math.round(totalProtein), 1300, 250);

  if (!open) return null;

  const onLog = () => {
    // Whey shakes and milk are NOT supplements (supplements = pills /
    // vitamins / creatine). Route them to the current meal slot instead so
    // they show up in the meal timeline like any other intake.
    const slot = autoMealSlot();
    let logged = false;
    if (addFoodEntry && selectedWhey && scoops > 0) {
      const wheyServingG = scoops * (selectedWhey.servingG || 30);
      const wheyLabel = `${scoops} scoop${scoops > 1 ? 's' : ''}`;
      addFoodEntry(selectedWhey, wheyServingG, wheyLabel, slot, today);
      logged = true;
    }
    if (addFoodEntry && milkOn && milkPour && selectedMilk) {
      addFoodEntry(selectedMilk, milkPour.grams, milkPour.label, slot, today);
      logged = true;
    }
    if (logged && onAfterLog) onAfterLog();
    else if (onClose) onClose();
  };

  const ctaLabel = (() => {
    const parts = [];
    if (scoops > 0) parts.push(`${scoops} scoop${scoops > 1 ? 's' : ''}`);
    if (milkOn) parts.push(`${milkVolumeMl}ml milk`);
    return parts.length ? `+ Add ${parts.join(' + ')}` : '+ Add';
  })();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Protein focus"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(4,7,5,0.86)', backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        display: 'flex', alignItems: 'stretch', justifyContent: 'center',
        animation: 'pf-fade 0.22s ease',
        fontFamily: FS, color: C.ink,
      }}
    >
      <style>{`
        @keyframes pf-fade{from{opacity:0}to{opacity:1}}
        @keyframes pf-slide{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}
        .pf-sheet{animation:pf-slide 0.32s cubic-bezier(0.22,0.61,0.36,1)}
        .pf-no-sb::-webkit-scrollbar{display:none}
        .pf-no-sb{scrollbar-width:none}
        .pf-cta:active{transform:scale(0.985)}
      `}</style>
      <div
        className="pf-sheet"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 480,
          background: 'radial-gradient(ellipse at 50% 0%, rgba(34,211,238,0.10), transparent 55%), #07090c',
          borderTopLeftRadius: 0, borderTopRightRadius: 0,
          display: 'flex', flexDirection: 'column',
          maxHeight: '100dvh', height: '100dvh', minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: 'calc(env(safe-area-inset-top, 0px) + 14px) 18px 10px',
          borderBottom: '1px solid rgba(34,211,238,0.16)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 8, height: 8, borderRadius: 8, background: C.cyan,
              boxShadow: `0 0 10px ${C.cyan}`,
            }} />
            <span style={{ fontFamily: FM, fontSize: 11.5, color: C.cyan, letterSpacing: '1.8px', textTransform: 'uppercase', fontWeight: 600 }}>
              Protein · Fuel the Day
            </span>
          </div>
          <button onClick={onClose} aria-label="Close"
            style={{
              width: 36, height: 36, borderRadius: 18,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: C.ink, cursor: 'pointer',
              display: 'grid', placeItems: 'center', fontSize: 20, lineHeight: 1, padding: 0,
            }}>×</button>
        </div>

        {/* Scrollable body */}
        <div className="pf-no-sb" style={{
          flex: 1, minHeight: 0, overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          padding: '14px 16px 16px',
        }}>

          {/* Hero ring */}
          <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 0 8px' }}>
            <HeroRing
              value={totalProtein}
              max={proteinGoal}
              color={goalHit ? C.green : C.cyan}
              size={236}
              sw={14}
              centerNode={
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontFamily: FM, fontSize: 9.5, color: C.faint, letterSpacing: '1.8px', textTransform: 'uppercase' }}>
                    Logged
                  </div>
                  <div style={{ fontFamily: FS, fontSize: 56, fontWeight: 200, color: C.ink, letterSpacing: '-2.4px', lineHeight: 1, marginTop: 3 }}>
                    {dispTotal}
                    <span style={{ fontFamily: FM, fontSize: 16, color: C.dim, fontWeight: 400, marginLeft: 4 }}>g</span>
                  </div>
                  <div style={{ marginTop: 6, fontFamily: FM, fontSize: 11, letterSpacing: '0.6px',
                    color: goalHit ? C.green : C.cyan, fontWeight: 600 }}>
                    {goalHit ? `GOAL HIT ✓` : `${Math.round(remaining)}g LEFT`}
                  </div>
                  <div style={{ fontFamily: FM, fontSize: 9, color: C.faint, marginTop: 3 }}>
                    of {proteinGoal}g goal
                  </div>
                </div>
              }
            />
          </div>

          {/* Ring legend — only shown when over goal */}
          {totalProtein > proteinGoal && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: -4, marginBottom: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 8, height: 8, borderRadius: 4, background: C.green }} />
                <span style={{ fontFamily: FM, fontSize: 9, color: C.faint, letterSpacing: '0.8px' }}>Goal ({proteinGoal}g)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 8, height: 8, borderRadius: 4, background: '#ff6b6b' }} />
                <span style={{ fontFamily: FM, fontSize: 9, color: C.faint, letterSpacing: '0.8px' }}>Over by {Math.round(totalProtein - proteinGoal)}g</span>
              </div>
            </div>
          )}

          {/* Source breakdown */}
          <div style={{ marginTop: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ width: 14, height: 1, background: C.cyan, opacity: 0.5 }} />
              <span style={{ fontFamily: FM, fontSize: 9.5, color: C.cyan, letterSpacing: '1.8px', textTransform: 'uppercase', opacity: 0.85 }}>
                Sources today
              </span>
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
              <span style={{ fontFamily: FM, fontSize: 9.5, color: C.faint }}>{Math.round(breakdownTotal)}g</span>
            </div>

            {breakdownTotal === 0 ? (
              <div style={{
                padding: '14px 12px', borderRadius: 10,
                border: '1px dashed rgba(255,255,255,0.1)',
                color: C.faint, fontFamily: FM, fontSize: 11, letterSpacing: '0.8px', textAlign: 'center',
              }}>
                No protein logged yet — let's bridge the gap.
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', height: 14, borderRadius: 8, overflow: 'hidden', gap: 1, background: 'rgba(255,255,255,0.05)' }}>
                  {slotBreakdown.map(b => b.grams > 0 && (
                    <div key={b.slot} title={`${SLOT_LABELS[b.slot]} · ${Math.round(b.grams)}g`}
                      style={{
                        flex: b.grams,
                        background: SLOT_COLORS[b.slot],
                        boxShadow: `inset 0 0 6px ${SLOT_COLORS[b.slot]}99`,
                      }} />
                  ))}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 10 }}>
                  {slotBreakdown.filter(b => b.grams > 0).map(b => (
                    <div key={b.slot} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 8, background: SLOT_COLORS[b.slot], boxShadow: `0 0 6px ${SLOT_COLORS[b.slot]}` }} />
                      <span style={{ fontFamily: FM, fontSize: 9.5, color: C.dim, letterSpacing: '1.2px', textTransform: 'uppercase', minWidth: 80 }}>
                        {SLOT_LABELS[b.slot]}
                      </span>
                      <span style={{ fontFamily: FS, fontSize: 12, color: C.ink, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {b.top.length ? b.top.map(t => `${t.name} ${t.p}g`).join(' · ') : '—'}
                      </span>
                      <span style={{ fontFamily: FM, fontSize: 12, color: C.ink, fontWeight: 600 }}>
                        {Math.round(b.grams * 10) / 10}g
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Goal hit celebration (if goal hit and not overridden) */}
          {goalHit && !overrideGoal && (
            <div style={{
              marginTop: 22, padding: '16px 14px', borderRadius: 14,
              background: 'rgba(0,230,118,0.08)', border: '1px solid rgba(0,230,118,0.35)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
            }}>
              <div style={{ fontSize: 28, color: C.green, lineHeight: 1, filter: `drop-shadow(0 0 8px ${C.green})` }}>✓</div>
              <div style={{ fontFamily: FS, fontSize: 18, fontWeight: 500, color: C.ink, letterSpacing: '-0.3px', textAlign: 'center' }}>
                Goal reached — alhamdulillah
              </div>
              <div style={{ fontFamily: FM, fontSize: 10, color: C.dim, letterSpacing: '0.8px', textAlign: 'center', maxWidth: 260 }}>
                You hit {Math.round(totalProtein)}g of your {proteinGoal}g protein goal today.
              </div>
              <button
                onClick={() => setOverrideGoal(true)}
                style={{
                  marginTop: 4, padding: '9px 16px', borderRadius: 99,
                  background: 'transparent', border: `1px solid ${C.cyan}55`,
                  color: C.cyan, fontFamily: FM, fontSize: 10.5, fontWeight: 600,
                  letterSpacing: '1.4px', textTransform: 'uppercase', cursor: 'pointer',
                }}>
                Add anyway
              </button>
            </div>
          )}

          {/* Whey picker + mixer + calculator */}
          {showCalculator && (
            <>
              {/* Whey picker */}
              <div style={{ marginTop: 22 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <div style={{ width: 14, height: 1, background: C.cyan, opacity: 0.5 }} />
                  <span style={{ fontFamily: FM, fontSize: 9.5, color: C.cyan, letterSpacing: '1.8px', textTransform: 'uppercase', opacity: 0.85 }}>
                    Pick your whey
                  </span>
                  <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
                </div>
                <div className="pf-no-sb" style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}>
                  {wheyProducts.map(w => {
                    const active = w.id === selectedWheyId;
                    return (
                      <button
                        key={w.id}
                        onClick={() => setSelectedWheyId(w.id)}
                        style={{
                          flexShrink: 0, width: 144, padding: '11px 12px', borderRadius: 12,
                          background: active ? 'rgba(34,211,238,0.10)' : 'rgba(255,255,255,0.03)',
                          border: active ? `1px solid ${C.cyan}` : '1px solid rgba(255,255,255,0.07)',
                          boxShadow: active ? `0 0 18px rgba(34,211,238,0.35)` : 'none',
                          color: C.ink, cursor: 'pointer', textAlign: 'left',
                          fontFamily: FS,
                          display: 'flex', flexDirection: 'column', gap: 4,
                          transition: 'background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
                        }}>
                        <span style={{ fontFamily: FM, fontSize: 8, color: C.faint, letterSpacing: '1.2px', textTransform: 'uppercase' }}>
                          {w.brand || ' '}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.2, minHeight: 28, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {w.name}
                        </span>
                        <span style={{
                          alignSelf: 'flex-start', marginTop: 2,
                          padding: '3px 8px', borderRadius: 99,
                          background: active ? C.cyan : 'rgba(34,211,238,0.18)',
                          color: active ? '#03110a' : C.cyan,
                          fontFamily: FM, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.6px',
                        }}>
                          {w.protein}g/scoop
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Milk mixer */}
              <div style={{ marginTop: 16, padding: 12, borderRadius: 14, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, cursor: 'pointer' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontFamily: FS, fontSize: 13.5, color: C.ink, fontWeight: 500 }}>Mix with milk</span>
                    <span style={{ fontFamily: FM, fontSize: 9, color: C.faint, letterSpacing: '0.6px' }}>
                      {milkPour ? `+${Math.round(milkPour.protein * 10) / 10}g protein · +${Math.round(milkPour.calories)} kcal` : 'Adds milk protein to the total'}
                    </span>
                  </div>
                  <button
                    onClick={() => setMilkOn(v => !v)}
                    role="switch"
                    aria-checked={milkOn}
                    style={{
                      width: 44, height: 26, borderRadius: 13,
                      background: milkOn ? C.cyan : 'rgba(255,255,255,0.12)',
                      border: 'none', cursor: 'pointer', position: 'relative',
                      transition: 'background 0.18s ease',
                      flexShrink: 0,
                    }}>
                    <span style={{
                      position: 'absolute', top: 3, left: milkOn ? 21 : 3,
                      width: 20, height: 20, borderRadius: 10, background: '#fff',
                      transition: 'left 0.18s ease',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    }} />
                  </button>
                </label>

                {milkOn && (
                  <>
                    <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
                      {milkProducts.map(m => {
                        const active = m.id === selectedMilkId;
                        return (
                          <button
                            key={m.id}
                            onClick={() => setSelectedMilkId(m.id)}
                            style={{
                              flex: 1, padding: '9px 6px', borderRadius: 10,
                              background: active ? 'rgba(34,211,238,0.12)' : 'rgba(255,255,255,0.04)',
                              border: active ? `1px solid ${C.cyan}` : '1px solid rgba(255,255,255,0.08)',
                              color: active ? C.cyan : C.dim,
                              fontFamily: FM, fontSize: 9.5, fontWeight: 600, letterSpacing: '0.6px',
                              cursor: 'pointer', textAlign: 'center', lineHeight: 1.2,
                            }}>
                            {(m.name || '').replace(/^Clover\s+/i, '').replace(/\s*\(.*\)$/, '')}
                          </button>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                      {VOLUMES.map(v => {
                        const active = v === milkVolumeMl;
                        return (
                          <button
                            key={v}
                            onClick={() => setMilkVolumeMl(v)}
                            style={{
                              flex: 1, padding: '9px 6px', borderRadius: 10,
                              background: active ? 'rgba(34,211,238,0.18)' : 'rgba(255,255,255,0.04)',
                              border: active ? `1px solid ${C.cyan}` : '1px solid rgba(255,255,255,0.08)',
                              color: active ? C.cyan : C.dim,
                              fontFamily: FM, fontSize: 11, fontWeight: 700, letterSpacing: '0.6px',
                              cursor: 'pointer',
                            }}>
                            {v}ml
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Calculator readout */}
              <div style={{
                marginTop: 16, padding: 14, borderRadius: 14,
                background: 'linear-gradient(135deg, rgba(34,211,238,0.08), rgba(34,211,238,0.02))',
                border: '1px solid rgba(34,211,238,0.25)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div>
                    <div style={{ fontFamily: FM, fontSize: 9, color: C.faint, letterSpacing: '1.4px', textTransform: 'uppercase' }}>
                      Whey scoops
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4 }}>
                      <span style={{ fontFamily: FS, fontSize: 40, fontWeight: 200, color: C.cyan, letterSpacing: '-1.5px', lineHeight: 1 }}>
                        {scoops}
                      </span>
                      <span style={{ fontFamily: FM, fontSize: 11, color: C.dim }}>
                        scoop{scoops === 1 ? '' : 's'}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      onClick={() => setScoopOverride(Math.max(0, scoops - 1))}
                      aria-label="Decrease scoops"
                      style={{
                        width: 38, height: 38, borderRadius: 12,
                        background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                        color: C.ink, fontSize: 22, fontWeight: 600, cursor: 'pointer',
                        lineHeight: 1, padding: 0,
                      }}>−</button>
                    <button
                      onClick={() => setScoopOverride(scoops + 1)}
                      aria-label="Increase scoops"
                      style={{
                        width: 38, height: 38, borderRadius: 12,
                        background: 'rgba(34,211,238,0.18)', border: `1px solid ${C.cyan}55`,
                        color: C.cyan, fontSize: 22, fontWeight: 600, cursor: 'pointer',
                        lineHeight: 1, padding: 0,
                      }}>+</button>
                  </div>
                </div>

                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ fontFamily: FM, fontSize: 11, color: C.dim, letterSpacing: '0.4px' }}>
                    {selectedWhey?.name || 'Whey'} = <span style={{ color: C.ink, fontWeight: 600 }}>{Math.round(scoops * wheyProteinPerScoop * 10) / 10}g</span> protein · {Math.round(scoops * wheyKcalPerScoop)} kcal
                  </div>
                  {milkOn && milkPour && selectedMilk && (
                    <div style={{ fontFamily: FM, fontSize: 11, color: C.dim, letterSpacing: '0.4px' }}>
                      + {milkVolumeMl}ml {(selectedMilk.name || '').replace(/^Clover\s+/i, '')} = <span style={{ color: C.ink, fontWeight: 600 }}>{Math.round(milkPour.protein * 10) / 10}g</span> protein · {Math.round(milkPour.calories)} kcal
                    </div>
                  )}
                </div>

                <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid rgba(34,211,238,0.18)', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontFamily: FM, fontSize: 9.5, color: C.cyan, letterSpacing: '1.4px', textTransform: 'uppercase', fontWeight: 700 }}>
                    Total
                  </span>
                  <span style={{ fontFamily: FM, fontSize: 13, color: C.ink, fontWeight: 600 }}>
                    +{Math.round(addedProtein * 10) / 10}g · +{Math.round(addedKcal)} kcal
                  </span>
                </div>
                <div style={{ marginTop: 4, fontFamily: FM, fontSize: 10, color: C.faint, letterSpacing: '0.4px', textAlign: 'right' }}>
                  After: {Math.round(projectedTotal)}g / {proteinGoal}g
                  {overshoot > 0.5 && <span style={{ color: C.amber, marginLeft: 6 }}>(+{Math.round(overshoot)}g over)</span>}
                </div>
              </div>

              {/* CTA */}
              <button
                onClick={onLog}
                className="pf-cta"
                style={{
                  width: '100%', marginTop: 14, padding: '15px 18px', borderRadius: 16,
                  background: `linear-gradient(135deg, ${C.cyan} 0%, #0fb6d4 100%)`,
                  border: 'none',
                  color: '#03110a', fontFamily: FS, fontSize: 16, fontWeight: 800, letterSpacing: '-0.2px',
                  cursor: 'pointer',
                  boxShadow: `0 6px 22px rgba(34,211,238,0.35), 0 0 0 1px rgba(34,211,238,0.5) inset, 0 -2px 0 rgba(0,0,0,0.18) inset`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  transition: 'transform 0.08s ease',
                }}>
                {ctaLabel}
              </button>
              <div style={{ marginTop: 8, textAlign: 'center', fontFamily: FM, fontSize: 9, color: C.faint, letterSpacing: '0.6px' }}>
                Logs to your current meal slot
              </div>
            </>
          )}

          {/* Bottom spacer: tab-bar height (~84px) + home-indicator inset so
              the CTA never sits beneath the floating tab pill. */}
          <div style={{ height: 'calc(env(safe-area-inset-bottom, 0px) + 96px)' }} />
        </div>
      </div>
    </div>
  );
}
